import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageImages } from "@/v2/lib/usePageImages";

const VALUES = [
  { t: "Slowness", d: "A hand-tufted rug takes weeks — sometimes months. We refuse to speed that up." },
  { t: "Fair looms", d: "Every artisan earns above the state weaving guild's benchmark, always." },
  { t: "Nothing wasted", d: "Yarn ends become basket trims. Wood offcuts become frame corners." },
  { t: "Signed forever", d: "Each piece is signed and numbered — traceable to a person, a village, a year." },
];

export default function V2About() {
  const { img } = usePageImages();
  return (
    <div data-testid="v2-about-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="overline-v2">The Iryaa Story</div>
          <h1 className="mt-5 font-serif-warm text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            A homegrown brand,<br/><em>with a heart for handmade.</em>
          </h1>
          <p className="mt-8 text-[16px] text-[#4a4640] leading-relaxed font-light max-w-xl">
            Founded by <b className="text-[#1a1a1a] font-medium">Alka</b>, a mother of two with a deep love for meaningful interiors, Iryaa Homes was created to bring beautifully handcrafted products into modern homes while celebrating the artisans behind every piece.
          </p>
        </div>
        <div className="lg:col-span-6">
          <img src={img("about.hero", "https://images.pexels.com/photos/37877827/pexels-photo-37877827.jpeg?auto=compress&cs=tinysrgb&w=1400")} alt="Iryaa artisan" className="w-full aspect-[4/5] object-cover" />
        </div>
      </section>

      {/* Why handmade */}
      <section className="bg-[#1a1a1a] text-[#fbf9f5]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline-v2 text-[#c9ac93]">Why handmade</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl leading-[1.05]">Because a machine can never learn to be gentle.</h2>
          </div>
          <div className="lg:col-span-7 space-y-8 text-[15px] text-white/75 font-light leading-relaxed">
            <p>Handmade rugs carry the touch of the person who made them. The pile is a little denser here, the border a little softer there — quiet imperfections that a factory strips away.</p>
            <p>We work with artisan clusters across Bhadohi and Rajasthan — homes where three generations often work on a single loom, and where the last inch of a rug is finished with the same care as the first.</p>
            <p>Every Iryaa piece is signed by the artisan who finished it. That signature isn't decoration — it's a promise.</p>
          </div>
        </div>
      </section>

      {/* Heritage */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 grid grid-cols-2 gap-4">
          <img src={img("about.loom", "https://images.pexels.com/photos/29848182/pexels-photo-29848182.jpeg?auto=compress&cs=tinysrgb&w=900")} alt="Loom" className="w-full aspect-[3/4] object-cover" />
          <img src={img("about.detail", "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?auto=format&fit=crop&w=900&q=80")} alt="Detail" className="w-full aspect-[3/4] object-cover mt-12" />
        </div>
        <div className="lg:col-span-6">
          <div className="overline-v2">Bhadohi · India's rug capital</div>
          <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl leading-[1.05]">Where the earth still whispers to the loom.</h2>
          <p className="mt-8 text-[15px] text-[#4a4640] leading-relaxed font-light max-w-lg">
            Bhadohi in Uttar Pradesh has been weaving rugs for over 500 years. The soft water of its rivers, the temperate air, and the muscle-memory passed down over generations make it one of the finest carpet-making regions in the world.
          </p>
          <p className="mt-4 text-[15px] text-[#4a4640] leading-relaxed font-light max-w-lg">
            We are proud to source most of our rugs from small family ateliers in and around this region — never factories.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#f2ede2] py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="mb-12">
            <div className="overline-v2">Values</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl">The things we will never rush.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v) => (
              <div key={v.t} className="border-t border-[#1a1a1a] pt-5">
                <div className="font-serif-warm text-3xl">{v.t}</div>
                <p className="mt-3 text-[14px] text-[#4a4640] leading-relaxed font-light">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 text-center">
        <div className="overline-v2">Come home to Iryaa</div>
        <h2 className="mt-4 font-serif-warm text-4xl md:text-6xl leading-[1.05] max-w-3xl mx-auto">Ready to bring one home?</h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/collections?cat=carpets" className="v2-btn-primary">Explore Rugs <ArrowRight size={14} /></Link>
          <Link to="/custom" className="v2-btn-outline">Commission Custom</Link>
        </div>
      </section>
    </div>
  );
}
